import {
  Button,
  DateTimeInput,
  Dialog,
  DialogDescription,
  DialogHeader,
  Grid,
  GridRow,
  ToastLevel,
  useIntl,
  useToast,
} from "@metsooutotec/modes-react-components";
import sensorStore from "features/iot/sensors/stores/sensor-store";
import {
  EditSensorDto,
  SensorTable,
} from "features/iot/sensors/models/sensor-models";
import { FC, useState } from "react";
import { convertDateToDDMMMYYYY, getTodayDateString } from "components/utils/date-formatter-utils";
import sensorBatchStore from "features/iot/sensor-batch/stores/sensor-batch-store";

interface SensorsToggleModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  sensorTable?: SensorTable[];
}
const SensorsToggleModal: FC<SensorsToggleModalProps> = ({
  isOpen,
  setIsOpen,
  sensorTable,
}) => {
  const { formatMessage } = useIntl();
  const [datePickerError, setDatePickerError] = useState<{
    isError: boolean;
    errorMessage: string;
  }>({ isError: false, errorMessage: "" });
  const [manufacturedDate, setManufacturedDate] = useState<string>(
    getTodayDateString()
  );
  const [isLoading, setLoading] = useState<boolean>(false);
  const { setMessage } = useToast();

  // If sensorsToEdit is null or undefined, render nothing
  if (!sensorTable) {
    return null;
  }

  const handleDateChange = (value: string) => {
    try {
      setDatePickerError({ isError: false, errorMessage: "" });
      const date = new Date(value);
      if (isNaN(date.getDate())) {
        setDatePickerError({ isError: true, errorMessage: "Invalid Date!" });
      } else {
        const formatedDate = convertDateToDDMMMYYYY(date);
        setManufacturedDate(formatedDate);
      }
    } catch (e) {
      console.log(e);
      setDatePickerError({
        isError: true,
        errorMessage: e instanceof Error ? e.message : "Invalid Date!"
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      setLoading(true);
      setDatePickerError({ isError: false, errorMessage: "" });

      const selectedRows = sensorTable.filter((s) => s.isChecked);
      if (selectedRows.length === 0) return;

      setLoading(true);

      const listOfEditSensorDto: EditSensorDto[] = selectedRows.map(
        (sensor): EditSensorDto => {
          const sensorBatch = sensorBatchStore.getSensorBatchBySensorMacAddress(
            sensor.macAddress
          );
          const sbSensor = sensorBatch?.sensors.find(
            (s) => s.macAddress === sensor.macAddress
          );

          return {
            sensorBatchId: sensorBatch?.id || "Empty",
            serialNumber: sbSensor?.serialNumber || "Empty",
            macAddress: sbSensor?.macAddress || "Empty",
            isManufactured: !sbSensor?.isManufactured,
            manufacturedDate: !sbSensor?.isManufactured
              ? manufacturedDate
              : getTodayDateString(),
            firmwareRevision: sbSensor?.firmwareRevision || "Empty",
            hardwareRevision: sbSensor?.hardwareRevision || "Empty",
            numberOfResistiveElements: sbSensor?.numberOfResistiveElements || 0,
            offset: sbSensor?.offset || 0,
            pitch: sbSensor?.pitch || 0,
          };
        }
      );

      const request = await sensorStore.toggleSensorsManuf(listOfEditSensorDto);
      if (request === 200) {
        setMessage(
          "Sensors Manifactured states has been toggled",
          ToastLevel.SUCCESS,
          6000
        );
        setIsOpen(false);
      } else {
        setMessage(
          "Something whent wrong when Sensors Manifactured states has been toggled!",
          ToastLevel.ALERT,
          6000
        );
      }
      sensorTable.forEach((s) => (s.isChecked = false));
      setLoading(false);
    } catch (error) {
      setDatePickerError({
        isError: true,
        errorMessage: "Form submission error, please read logs",
      });
      console.error("Form submission error:", error);
    } finally {
      setLoading(false);
    }
  };
  const onCloseProcess = (): void => {
    setDatePickerError({ isError: false, errorMessage: "" });
    setIsOpen(false);
  };
  const labels = {
    confirm: formatMessage("modal.confirm"),
    cancel: formatMessage("modal.cancel"),
    header: formatMessage("sensorModal.toggleTitle"),
    description: formatMessage("sensorsModal.descriptionToggle"),
    manufacturedDate: formatMessage("sensorsModal.manufacturedDate"),
  };
  const renderButtons = (): React.ReactNode => {
    const buttonsConfig = [
      { label: labels.confirm, onClick: handleSubmit, primary: true },
      { label: labels.cancel, onClick: onCloseProcess, primary: false },
    ];
    return buttonsConfig.map((button, index) => {
      const { label, onClick, primary } = button;
      const buttonGridStyle =
        index === 0
          ? {
              marginBottom: "var(--size-m)",
            }
          : {};
      return (
        <GridRow key={label} style={buttonGridStyle}>
          <Button
            loading={isLoading}
            disabled={isLoading}
            onClick={onClick}
            flexible={true}
            children={label}
            centered
            size={"medium"}
            primary={primary}
          />
        </GridRow>
      );
    });
  };
  return (
    <Dialog dimmer open={isOpen} size={"small"}>
      <DialogHeader username={""}>
        {labels.header}{" "}
        <span style={{ color: "red" }}>
          # {sensorTable.filter((s) => s.isChecked).length}
        </span>
      </DialogHeader>
      {datePickerError.isError && (
        <p style={{ color: "red" }}>{datePickerError.errorMessage}</p>
      )}
      <DialogDescription style={{ marginBottom: "var(--size-xxl)" }}>
        {labels.description}
      </DialogDescription>
      <Grid columns={2}>
        <GridRow>
          <DateTimeInput
            label={labels.manufacturedDate}
            onChange={(value: string | any) =>
              handleDateChange(value.toString())
            }
            value={manufacturedDate}
            format="DD-MMM-YYYY"
            timeFormat={false}
            error={datePickerError.errorMessage}
          />
        </GridRow>
        {renderButtons()}
      </Grid>
    </Dialog>
  );
};
export default SensorsToggleModal;
